import { useEffect, useRef } from 'react';

import type { UserPreferences, UserPreferencesProvider } from '@atlaskit/editor-common/types';
import type { UserPreferencesProvider as UserPreferencesProviderNext } from '@atlaskit/editor-common/user-preferences';
import { editorExperiment } from '@atlaskit/tmp-editor-statsig/experiments';

import { fg } from '@confluence/feature-gating';
import { getApolloClient } from '@confluence/graphql/entry-points/getApolloClient';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData';
import { graphql } from 'react-relay';
import { queryInterop } from '@confluence/apollo-relay-interop/entry-points/query';
import { mutateInterop } from '@confluence/apollo-relay-interop/entry-points/mutate';
import { writeQueryInterop } from '@confluence/apollo-relay-interop/entry-points/writeQuery';

import { EditorUserPreferencesProvider } from './EditorUserPreferencesProvider';
// eslint-disable-next-line import/no-cycle
import { getUserPreferencesProvider, UserPreferencesPersistenceAPI } from './EditorUserPreferences';
import type {
	EditorPreferencesQuery$data,
	EditorPreferencesQuery$variables,
} from './__generated__/EditorPreferencesQuery.graphql';
import type {
	EditorPreferencesMutation$data,
	EditorPreferencesMutation$variables,
} from './__generated__/EditorPreferencesMutation.graphql';

const EditorPreferencesQuery = graphql`
	query EditorPreferencesQuery {
		userPreferences {
			confluenceEditorSettings {
				toolbarDockingInitialPosition
			}
		}
	}
`;

const EditorPreferencesMutation = graphql`
	mutation EditorPreferencesMutation($toolbarDockingInitialPosition: String!) {
		updateUserPreferences(
			userPreferences: {
				confluenceEditorSettingsInput: {
					toolbarDockingInitialPosition: $toolbarDockingInitialPosition
				}
			}
		) {
			confluenceEditorSettings {
				toolbarDockingInitialPosition
			}
		}
	}
`;

export const parseConfluenceEditorSettings = (confluenceEditorSettings?: any) => {
	if (confluenceEditorSettings && (confluenceEditorSettings satisfies UserPreferences)) {
		return confluenceEditorSettings as UserPreferences;
	}

	return {} as UserPreferences;
};

export class ConfluenceEditorPreferencesProvider implements UserPreferencesProvider {
	private userPreferences: UserPreferences = {};
	// We use getApolloClient to get the apollo client instance
	// This is to avoid adding required apollo context
	private apolloClient = getApolloClient();
	private initialized = false;

	public constructor() {
		this.init();
	}

	public init() {
		if (!this.initialized) {
			this.fetchPreferences().catch(() => {
				// Do nothing
			});
		}
	}

	// add this to implment the UserPreferensProvider interface.
	public loadPreferences(): Promise<UserPreferences> {
		return Promise.reject();
	}

	private async fetchPreferences(): Promise<void> {
		if (!this.apolloClient) {
			return Promise.reject('No apollo client');
		}

		try {
			if (!this.initialized) {
				const { data } = await queryInterop<
					EditorPreferencesQuery$data,
					EditorPreferencesQuery$variables
				>({
					query: EditorPreferencesQuery,
				});
				this.userPreferences = parseConfluenceEditorSettings(
					data?.userPreferences?.confluenceEditorSettings,
				);
				this.initialized = true;
			}
			return Promise.resolve();
		} catch (error) {
			return Promise.reject(error);
		}
	}

	async updatePreference<K extends keyof UserPreferences>(
		key: K,
		value: UserPreferences[K],
	): Promise<void> {
		if (!this.apolloClient) {
			return Promise.reject('No apollo client');
		}

		this.userPreferences[key] = value;

		const variables = {
			[key as keyof EditorPreferencesMutation$variables]: value,
		} as EditorPreferencesMutation$variables;

		try {
			const response = await mutateInterop<
				EditorPreferencesMutation$data,
				EditorPreferencesMutation$variables
			>({
				mutation: EditorPreferencesMutation,
				variables,
				update: () => {
					// this is required to indicate toolbar changes to progressive object header
					writeQueryInterop({
						query: EditorPreferencesQuery,
						data: {
							userPreferences: {
								__typename: 'UserPreferences',
								confluenceEditorSettings: {
									__typename: 'ConfluenceEditorSettings',
									toolbarDockingInitialPosition: value === 'top' ? value : null, // Null is required since apollo cache uses shallow reference equality
								},
							},
						},
					});
				},
			});

			const confluenceEditorSettings = parseConfluenceEditorSettings(
				response.data?.updateUserPreferences?.confluenceEditorSettings,
			);

			this.userPreferences[key] = confluenceEditorSettings[key];

			return Promise.resolve();
		} catch (error) {
			return Promise.reject(error);
		}
	}

	getPreference<K extends keyof UserPreferences>(key: K): UserPreferences[K] | undefined | null {
		return this.userPreferences[key];
	}
}

const createUserPreferencesProvider = (options?: {
	getCurrentIsLivePage?: () => boolean;
}): UserPreferencesProvider => {
	if (fg('platform_editor_user_preferences_provider_update')) {
		const getFallbackUserPreferences = () =>
			({
				toolbarDockingInitialPosition: options?.getCurrentIsLivePage?.() ? 'none' : 'top',
			}) as UserPreferences;

		return new EditorUserPreferencesProvider({ getFallbackUserPreferences });
	}
	return new ConfluenceEditorPreferencesProvider();
};

export const useEditorPreferencesProvider = (options?: {
	getCurrentIsLivePage?: () => boolean;
}) => {
	const { isLoggedIn } = useSessionData();

	const confluenceEditorPreferencesProvider = useRef<UserPreferencesProvider | undefined>();
	const editorPreferencesProvider = useRef<UserPreferencesProviderNext | undefined>();
	const persistenceAPI = useRef<UserPreferencesPersistenceAPI | undefined>();

	if (editorExperiment('platform_editor_controls', 'variant1')) {
		if (fg('platform_editor_use_preferences_plugin')) {
			if (!editorPreferencesProvider.current) {
				const isLivePage = options?.getCurrentIsLivePage?.() ?? false;
				persistenceAPI.current = new UserPreferencesPersistenceAPI(isLoggedIn);
				editorPreferencesProvider.current = getUserPreferencesProvider({
					isLivePage,
					persistenceAPI: persistenceAPI.current,
				});
			}
		} else if (!confluenceEditorPreferencesProvider.current) {
			confluenceEditorPreferencesProvider.current = isLoggedIn
				? createUserPreferencesProvider(options)
				: undefined;
		}
	}

	useEffect(() => {
		if (editorExperiment('platform_editor_controls', 'variant1')) {
			if (persistenceAPI.current && fg('platform_editor_use_preferences_plugin')) {
				persistenceAPI.current.setIsLoggedIn(isLoggedIn);
			}
		}
	}, [isLoggedIn]);

	return {
		confluenceEditorPreferencesProvider: confluenceEditorPreferencesProvider.current,
		editorPreferencesProvider: editorPreferencesProvider.current ?? undefined,
	};
};
